// PoliceAssistant - Assistant interactif pour Policiers et OPJ
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  UserSearch, HandMetal, Lock, FileText, Clock, 
  UserCheck, AlertTriangle, ArrowRight, Search,
  User, Phone, MapPin, Calendar, FileWarning,
  CheckCircle, XCircle, Loader2, History, Shield,
  Unlock, ArrowRightLeft, Plus
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// Actions disponibles selon le rôle
const POLICE_ACTIONS = [
  {
    id: 'interpellation',
    icon: UserSearch,
    title: 'Interpellation',
    description: 'Vérifier ou enregistrer le passage d\'un citoyen au bureau',
    color: 'bg-blue-500',
    roles: ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL']
  },
  {
    id: 'arrestation',
    icon: HandMetal,
    title: 'Arrestation',
    description: 'Mettre une personne en détention provisoire (max 48h)',
    color: 'bg-orange-500',
    roles: ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL']
  },
  {
    id: 'consultation',
    icon: Search,
    title: 'Consultation',
    description: 'Rechercher un citoyen et voir son historique',
    color: 'bg-green-500',
    roles: ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL']
  }
];

const OPJ_ACTIONS = [
  {
    id: 'prolongation',
    icon: Clock,
    title: 'Demande de Prolongation',
    description: 'Soumettre une demande de prolongation au Procureur',
    color: 'bg-purple-500',
    roles: ['SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE', 'PROCUREUR_GENERAL', 'COMMISSAIRE_PRINCIPAL']
  },
  {
    id: 'liberation',
    icon: Unlock,
    title: 'Libération',
    description: 'Ordonner la libération d\'un détenu',
    color: 'bg-emerald-500',
    roles: ['OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL', 'JUGE_PAIX', 'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE']
  },
  {
    id: 'transfert',
    icon: ArrowRightLeft,
    title: 'Transfert',
    description: 'Transférer un détenu vers une autre structure',
    color: 'bg-indigo-500',
    roles: ['OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL', 'DIRECTEUR_PRISON']
  }
];

const MOTIFS_INTERPELLATION = [
  'Contrôle d\'identité',
  'Vérification de routine',
  'Suspicion de vol',
  'Trouble à l\'ordre public',
  'Témoin potentiel',
  'Convocation',
  'Autre'
];

const MOTIFS_ARRESTATION = [
  'Flagrant délit',
  'Mandat d\'arrêt',
  'Mandat d\'amener',
  'Suspicion de crime',
  'Suspicion de délit',
  'Trouble grave à l\'ordre public',
  'Autre'
];

const PoliceAssistant = ({ isOpen, onClose, onActionComplete }) => {
  const { user, language } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // choose, search, action, confirm
  const [selectedAction, setSelectedAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personHistory, setPersonHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form data for different actions
  const [formData, setFormData] = useState({
    motif: '',
    motif_detail: '',
    lieu: '',
    notes: '',
    duree_prolongation: 24,
    structure_destination: ''
  });

  const userRole = user?.role || 'CITIZEN';

  // Get available actions based on role
  const availableActions = [
    ...POLICE_ACTIONS.filter(a => a.roles.includes(userRole)),
    ...OPJ_ACTIONS.filter(a => a.roles.includes(userRole))
  ];

  const isOPJ = ['OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL', 'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE', 'JUGE_PAIX'].includes(userRole);

  // Search for citizens
  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/police/search-citizen?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to users search
      try {
        const fallback = await api.get(`/users/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
        setSearchResults(fallback.data || []);
      } catch (e) {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load person history
  const loadPersonHistory = async (personId) => {
    try {
      const response = await api.get(`/police/citizen-history/${personId}`);
      setPersonHistory(response.data || []);
    } catch (error) {
      console.error('History error:', error);
      setPersonHistory([]);
    }
  };

  // Select a person
  const handleSelectPerson = async (person) => {
    setSelectedPerson(person);
    await loadPersonHistory(person.user_id || person.citizen_id || person.id);
    setStep('action');
  };

  // Create new person if not found
  const handleCreateNewPerson = () => {
    setSelectedPerson({
      isNew: true,
      name: searchQuery,
      first_name: searchQuery.split(' ')[0] || '',
      last_name: searchQuery.split(' ').slice(1).join(' ') || ''
    });
    setStep('action');
  };

  // Submit action
  const handleSubmit = async () => {
    if (!selectedAction || !selectedPerson) return;

    setSubmitting(true);
    try {
      let endpoint = '';
      let payload = {
        person_id: selectedPerson.user_id || selectedPerson.citizen_id || selectedPerson.id,
        person_name: selectedPerson.name || `${selectedPerson.first_name} ${selectedPerson.last_name}`,
        person_national_id: selectedPerson.national_id,
        ...formData
      };

      // If new person, include their details
      if (selectedPerson.isNew) {
        payload.create_person = true;
        payload.first_name = selectedPerson.first_name;
        payload.last_name = selectedPerson.last_name;
      }

      switch (selectedAction.id) {
        case 'interpellation':
          endpoint = '/police/interpellation';
          break;
        case 'arrestation':
          endpoint = '/police/arrestation';
          break;
        case 'prolongation':
          endpoint = '/police/prolongation';
          break;
        case 'liberation':
          endpoint = '/police/liberation';
          break;
        case 'transfert':
          endpoint = '/police/transfert';
          break;
        case 'consultation':
          // Consultation is view-only, just show success and close
          toast.success('Consultation terminée');
          resetForm();
          onClose();
          return;
        default:
          throw new Error('Action non reconnue');
      }

      const response = await api.post(endpoint, payload);
      
      toast.success(
        selectedAction.id === 'interpellation' ? 'Interpellation enregistrée' :
        selectedAction.id === 'arrestation' ? 'Arrestation enregistrée' :
        selectedAction.id === 'prolongation' ? 'Prolongation accordée' :
        selectedAction.id === 'liberation' ? 'Libération ordonnée' :
        'Action enregistrée'
      );

      // Reset and close
      resetForm();
      onActionComplete?.(response.data);
      onClose();

    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep('choose');
    setSelectedAction(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedPerson(null);
    setPersonHistory([]);
    setFormData({
      motif: '',
      motif_detail: '',
      lieu: '',
      notes: '',
      duree_prolongation: 24,
      structure_destination: ''
    });
  };

  // Render action selection
  const renderActionSelection = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Shield className="w-12 h-12 mx-auto text-primary mb-2" />
        <h3 className="text-lg font-semibold">
          {language === 'fr' ? 'Que souhaitez-vous faire ?' : 'What would you like to do?'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {isOPJ ? 'Actions OPJ disponibles' : 'Actions policier disponibles'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {availableActions.map((action) => {
          const Icon = action.icon;
          return (
            <Card 
              key={action.id}
              className="cursor-pointer hover:border-primary transition-all hover:shadow-md"
              onClick={() => {
                setSelectedAction(action);
                setStep('search');
              }}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`p-2 rounded-lg ${action.color} text-white`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">{action.title}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {action.description}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  // Render person search
  const renderPersonSearch = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => setStep('choose')}>
          ← Retour
        </Button>
        <Badge className={selectedAction.color}>
          {selectedAction.title}
        </Badge>
      </div>

      <div className="space-y-3">
        <Label>{language === 'fr' ? 'Rechercher la personne' : 'Search person'}</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Nom, prénom, UNJI ou téléphone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            data-testid="person-search-input"
          />
          <Button onClick={handleSearch} disabled={loading || searchQuery.length < 2}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          <Label className="text-xs text-muted-foreground">
            {searchResults.length} résultat(s) trouvé(s)
          </Label>
          {searchResults.map((person) => (
            <Card 
              key={person.user_id || person.id}
              className="cursor-pointer hover:border-primary"
              onClick={() => handleSelectPerson(person)}
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {person.name || `${person.first_name} ${person.last_name}`}
                  </p>
                  <div className="flex gap-2 text-xs text-muted-foreground">
                    {person.national_id && <span>UNJI: {person.national_id}</span>}
                    {person.phone_number && <span>• {person.phone_number}</span>}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No results - option to create */}
      {searchQuery.length >= 2 && !loading && searchResults.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-4 text-center">
            <UserSearch className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              Aucune personne trouvée pour "{searchQuery}"
            </p>
            <Button variant="outline" onClick={handleCreateNewPerson}>
              <Plus className="w-4 h-4 mr-2" />
              Créer cette personne
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render action form
  const renderActionForm = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => setStep('search')}>
          ← Retour
        </Button>
        <Badge className={selectedAction.color}>
          {selectedAction.title}
        </Badge>
      </div>

      {/* Selected person info */}
      <Card className="bg-muted/50">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">
                {selectedPerson.name || `${selectedPerson.first_name} ${selectedPerson.last_name}`}
              </p>
              {selectedPerson.national_id && (
                <p className="text-xs text-muted-foreground">UNJI: {selectedPerson.national_id}</p>
              )}
              {selectedPerson.isNew && (
                <Badge variant="outline" className="text-xs mt-1">Nouvelle personne</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Person history */}
      {personHistory.length > 0 && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Historique ({personHistory.length} entrée(s))
            </span>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {personHistory.map((entry, idx) => (
              <div key={idx} className="text-xs flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
                <History className="w-3 h-3" />
                <span>{entry.date} - {entry.type}: {entry.motif}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action-specific form */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="details">Détails</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-3 mt-3">
          {/* Motif */}
          <div className="space-y-2">
            <Label>Motif *</Label>
            <Select
              value={formData.motif}
              onValueChange={(val) => setFormData({ ...formData, motif: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un motif" />
              </SelectTrigger>
              <SelectContent>
                {(selectedAction.id === 'interpellation' ? MOTIFS_INTERPELLATION : MOTIFS_ARRESTATION).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.motif === 'Autre' && (
            <div className="space-y-2">
              <Label>Préciser le motif *</Label>
              <Input
                value={formData.motif_detail}
                onChange={(e) => setFormData({ ...formData, motif_detail: e.target.value })}
                placeholder="Détails du motif..."
              />
            </div>
          )}

          {/* Lieu */}
          <div className="space-y-2">
            <Label>Lieu de {selectedAction.id === 'interpellation' ? 'l\'interpellation' : 'l\'arrestation'}</Label>
            <Input
              value={formData.lieu}
              onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
              placeholder="Adresse ou description du lieu..."
            />
          </div>

          {/* Prolongation specific */}
          {selectedAction.id === 'prolongation' && (
            <div className="space-y-2">
              <Label>Durée de prolongation (heures)</Label>
              <Select
                value={formData.duree_prolongation.toString()}
                onValueChange={(val) => setFormData({ ...formData, duree_prolongation: parseInt(val) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 heures</SelectItem>
                  <SelectItem value="48">48 heures</SelectItem>
                  <SelectItem value="72">72 heures (autorisation parquet)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-3">
          <div className="space-y-2">
            <Label>Notes additionnelles</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observations, circonstances, témoins..."
              rows={4}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {language === 'fr' ? 'Assistant Police' : 'Police Assistant'}
          </DialogTitle>
          <DialogDescription>
            {isOPJ ? 'Actions OPJ et Police' : 'Actions de police'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {step === 'choose' && renderActionSelection()}
          {step === 'search' && renderPersonSearch()}
          {step === 'action' && renderActionForm()}
        </div>

        {step === 'action' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('search')}>
              Annuler
            </Button>
            {selectedAction.id === 'consultation' ? (
              <Button onClick={() => { resetForm(); onClose(); }} data-testid="close-consultation-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Fermer la consultation
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || (!formData.motif && selectedAction.id !== 'liberation' && selectedAction.id !== 'transfert')}
                className={selectedAction.color}
                data-testid="submit-action-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedAction.id === 'interpellation' && 'Enregistrer l\'interpellation'}
                {selectedAction.id === 'arrestation' && 'Procéder à l\'arrestation'}
                {selectedAction.id === 'prolongation' && 'Soumettre au Procureur'}
                {selectedAction.id === 'liberation' && 'Ordonner la libération'}
                {selectedAction.id === 'transfert' && 'Ordonner le transfert'}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PoliceAssistant;
